// ./src/components/features/Travels/TravelWaypointsDrawer/waypointsMapUtils.ts
import { Expense } from "@/utils/api";
import { EXPENSE_TYPES } from "@/config";

// ============================================
// Types
// ============================================

export interface MarkerConfig {
  color: string;
  glyphColor: string;
  label: string;
}

export interface WaypointWithIndex {
  expense: Expense;
  index: number;
  isFirst: boolean;
  isLast: boolean;
}

export interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

// ============================================
// Color Constants
// ============================================

/**
 * Marker colors based on expense type
 * These match the section colors in the drawer
 */
export const MARKER_COLORS = {
  // Travel waypoints - blue with position-based variations
  WAYPOINT_START: "#40c057", // green for start
  WAYPOINT_END: "#fa5252", // red for end
  WAYPOINT_STOP: "#228be6", // blue for intermediate stops

  // Other expense types
  REFUEL: "#fd7e14", // orange
  EXPENSE: "#fa5252", // red
  CHECKPOINT: "#868e96", // gray
  REVENUE: "#40c057", // green
} as const;

/**
 * Glyph (text/icon) colors for markers
 */
export const GLYPH_COLORS = {
  LIGHT: "#ffffff",
  DARK: "#1a1a1a",
} as const;

// ============================================
// Marker Configuration
// ============================================

/**
 * Get marker configuration based on expense type and position
 */
export const getMarkerConfig = (
  expense: Expense,
  index: number,
  total: number,
  isWaypoint: boolean
): MarkerConfig => {
  const isFirst = index === 0;
  const isLast = index === total - 1;

  // For waypoints (travel points), use position-based colors
  if (isWaypoint || expense.expenseType === EXPENSE_TYPES.TRAVEL_POINT) {
    if (isFirst) {
      return {
        color: MARKER_COLORS.WAYPOINT_START,
        glyphColor: GLYPH_COLORS.LIGHT,
        label: "S", // Start
      };
    }
    if (isLast) {
      return {
        color: MARKER_COLORS.WAYPOINT_END,
        glyphColor: GLYPH_COLORS.LIGHT,
        label: "E", // End
      };
    }
    return {
      color: MARKER_COLORS.WAYPOINT_STOP,
      glyphColor: GLYPH_COLORS.LIGHT,
      label: String(index),
    };
  }

  // For other expense types
  switch (expense.expenseType) {
    case EXPENSE_TYPES.REFUEL:
      return {
        color: MARKER_COLORS.REFUEL,
        glyphColor: GLYPH_COLORS.LIGHT,
        label: "⛽",
      };
    case EXPENSE_TYPES.EXPENSE:
      return {
        color: MARKER_COLORS.EXPENSE,
        glyphColor: GLYPH_COLORS.LIGHT,
        label: "$",
      };
    case EXPENSE_TYPES.CHECKPOINT:
      return {
        color: MARKER_COLORS.CHECKPOINT,
        glyphColor: GLYPH_COLORS.LIGHT,
        label: "📍",
      };
    case EXPENSE_TYPES.REVENUE:
      return {
        color: MARKER_COLORS.REVENUE,
        glyphColor: GLYPH_COLORS.LIGHT,
        label: "+",
      };
    default:
      return {
        color: MARKER_COLORS.WAYPOINT_STOP,
        glyphColor: GLYPH_COLORS.LIGHT,
        label: String(index + 1),
      };
  }
};

/**
 * Get the highlight color for a marker (used on hover)
 */
export const getHighlightColor = (baseColor: string): string => {
  // Return a lighter version of the color for highlight effect
  // This is a simple approach - you could also use color manipulation libraries
  return baseColor;
};

// ============================================
// Coordinate Helpers
// ============================================

export interface GeocodedCoordinates {
  lat: number;
  lng: number;
}

export interface GeocodedExpense {
  expense: Expense;
  coordinates: GeocodedCoordinates;
  index: number;
  isFirst: boolean;
  isLast: boolean;
}

/**
 * Parse location string to get coordinates
 * Supports formats like "lat,lng"
 */
export const parseCoordinates = (
  location: string | undefined
): GeocodedCoordinates | null => {
  if (!location) return null;

  // Try to parse "lat,lng" format (just two numbers separated by comma)
  const parts = location.split(",").map((p) => p.trim());
  if (parts.length === 2) {
    const lat = parseFloat(parts[0]);
    const lng = parseFloat(parts[1]);
    if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      return { lat, lng };
    }
  }

  return null;
};

/**
 * Check if a location string looks like an address (not coordinates)
 */
export const isAddressString = (location: string | undefined): boolean => {
  if (!location) return false;

  // If it parses as coordinates, it's not an address
  if (parseCoordinates(location)) return false;

  // Must have at least some alphabetic characters to be an address
  return /[a-zA-Z]/.test(location);
};

/**
 * Extract coordinates from expense
 * Uses latitude/longitude fields if available
 */
export const getExpenseCoordinates = (
  expense: Expense
): GeocodedCoordinates | null => {
  // Use latitude/longitude fields
  if (
    expense.latitude !== undefined &&
    expense.latitude !== null &&
    expense.longitude !== undefined &&
    expense.longitude !== null
  ) {
    const lat = typeof expense.latitude === 'string'
      ? parseFloat(expense.latitude)
      : expense.latitude;
    const lng = typeof expense.longitude === 'string'
      ? parseFloat(expense.longitude)
      : expense.longitude;

    if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      return { lat, lng };
    }
  }

  return null;
};

/**
 * Check if expense has valid coordinates
 */
export const hasCoordinates = (expense: Expense): boolean => {
  return getExpenseCoordinates(expense) !== null;
};

/**
 * Filter expenses that have valid coordinates
 */
export const getExpensesWithCoordinates = (
  expenses: Expense[]
): WaypointWithIndex[] => {
  const result: WaypointWithIndex[] = [];

  expenses.forEach((expense, index) => {
    const coords = getExpenseCoordinates(expense);
    if (coords) {
      result.push({
        expense,
        index,
        isFirst: index === 0,
        isLast: index === expenses.length - 1,
      });
    }
  });

  // Update isFirst/isLast based on filtered results
  return result.map((item, idx, arr) => ({
    ...item,
    isFirst: idx === 0,
    isLast: idx === arr.length - 1,
  }));
};

/**
 * Calculate bounds that fit all waypoints
 */
export const calculateBounds = (
  waypoints: WaypointWithIndex[]
): MapBounds | null => {
  if (waypoints.length === 0) return null;

  const coordinates = waypoints
    .map((w) => getExpenseCoordinates(w.expense))
    .filter((c): c is { lat: number; lng: number } => c !== null);

  if (coordinates.length === 0) return null;

  const lats = coordinates.map((c) => c.lat);
  const lngs = coordinates.map((c) => c.lng);

  return {
    north: Math.max(...lats),
    south: Math.min(...lats),
    east: Math.max(...lngs),
    west: Math.min(...lngs),
  };
};

/**
 * Add padding to bounds for better map display
 */
export const padBounds = (bounds: MapBounds, paddingPercent: number = 0.1): MapBounds => {
  const latPadding = (bounds.north - bounds.south) * paddingPercent;
  const lngPadding = (bounds.east - bounds.west) * paddingPercent;

  // Ensure minimum padding for single point or very close points
  const minPadding = 0.01; // roughly 1km

  return {
    north: bounds.north + Math.max(latPadding, minPadding),
    south: bounds.south - Math.max(latPadding, minPadding),
    east: bounds.east + Math.max(lngPadding, minPadding),
    west: bounds.west - Math.max(lngPadding, minPadding),
  };
};

// ============================================
// Route Line Helpers
// ============================================

/**
 * Create path coordinates for route polyline
 * Only includes waypoints (travel points), not other expense types
 */
export const createRoutePath = (
  waypoints: WaypointWithIndex[]
): Array<{ lat: number; lng: number }> => {
  return waypoints
    .filter((w) => w.expense.expenseType === EXPENSE_TYPES.TRAVEL_POINT)
    .map((w) => getExpenseCoordinates(w.expense))
    .filter((c): c is { lat: number; lng: number } => c !== null);
};

/**
 * Route line styling
 */
export const ROUTE_LINE_STYLE = {
  strokeColor: "#228be6",
  strokeOpacity: 0.8,
  strokeWeight: 3,
  geodesic: true,
} as const;

// ============================================
// Map Configuration
// ============================================

export const DEFAULT_MAP_OPTIONS = {
  mapTypeControl: true,
  mapTypeControlOptions: {
    position: 3, // google.maps.ControlPosition.TOP_RIGHT
    style: 1, // google.maps.MapTypeControlStyle.DROPDOWN_MENU
  },
  streetViewControl: false,
  fullscreenControl: false,
  zoomControl: true,
  gestureHandling: "greedy" as const,
} as const;

export const DEFAULT_CENTER = {
  lat: 40.7128,
  lng: -74.006,
}; // New York as fallback

export const DEFAULT_ZOOM = 12;

// Add this function before the ROUTE_LINE_STYLE constant

/**
 * Decode a Google Encoded Polyline string into an array of lat/lng coordinates.
 * Implements the standard Google Polyline encoding algorithm.
 */
export const decodePolyline = (
  encoded: string
): Array<{ lat: number; lng: number }> => {
  const result: Array<{ lat: number; lng: number }> = [];
  let index = 0;
  let lat = 0;
  let lng = 0;

  while (index < encoded.length) {
    let shift = 0;
    let resultVal = 0;
    let byte: number;

    do {
      byte = encoded.charCodeAt(index++) - 63;
      resultVal |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);
    lat += resultVal & 1 ? ~(resultVal >> 1) : resultVal >> 1;

    shift = 0;
    resultVal = 0;

    do {
      byte = encoded.charCodeAt(index++) - 63;
      resultVal |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);
    lng += resultVal & 1 ? ~(resultVal >> 1) : resultVal >> 1;

    result.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }

  return result;
};